import Image from "next/image";
import { useMemo, useState } from "react";

import { useGlobalData } from "@/providers";
import { Product } from "@generated/types";

type CurrencyFlagProps = {
  currency: string;
  size?: string;
  className?: string;
  flatColor?: boolean;
};

const CURRENCY_EXCEPTIONS = ["XAF", "XCD", "XOF"];

export const getIconPath = (products: Product[], currency: string) => {
  const product = products.find((p) => p.code === currency);

  if (product?.decimalPlaces && product?.decimalPlaces > 2) {
    return `/icons/crypto-png/${currency.toLowerCase()}.png`;
  }
  if (CURRENCY_EXCEPTIONS.includes(currency)) {
    return `/icons/currencies/${currency.toLowerCase()}.svg`;
  }
  return `/icons/countries/${currency.toLowerCase().slice(0, 2)}.svg`;
};

const CurrencyFlag = ({
  currency,
  size = "28px",
  className = "",
  flatColor = false,
}: CurrencyFlagProps) => {
  const { products } = useGlobalData();
  const [hasError, setHasError] = useState(false);

  // Memoize calculations
  const { iconPath, sizeNumber } = useMemo(() => {
    const path = getIconPath(products || [], currency);
    const num = Number(size.replace("px", ""));
    return { iconPath: path, sizeNumber: num };
  }, [products, currency, size]);

  if (!currency) {
    return null;
  }

  // Fallback component
  if (hasError) {
    return (
      <div
        className={`bg-brand-strong flex items-center justify-center text-white font-semibold rounded-full relative overflow-hidden flex-shrink-0 ${className}`}
        style={{
          width: size,
          height: size,
          fontSize: `${sizeNumber * 0.3}px`,
        }}
      >
        {currency.toUpperCase()}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/30 via-transparent to-transparent pointer-events-none" />
      </div>
    );
  }

  return (
    <div
      className={`relative flex-shrink-0 bg-brand-primary/10 rounded-full ${className}`}
      style={{ width: size, height: size }}
    >
      <Image
        alt={`${currency} flag`}
        className="rounded-full block object-cover"
        src={iconPath}
        onError={() => setHasError(true)}
        width={sizeNumber}
        height={sizeNumber}
        // `loading="eager"` without `priority`: flags render in long lists
        // (accounts, currency pickers), and `priority` marks every one of
        // them high-fetch-priority, competing with the whitelabel logo and
        // the app's JS for first paint.
        unoptimized
        loading="eager"
      />
      {!flatColor && (
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: `
              radial-gradient(
                ellipse at 30% 30%,
                rgba(255, 255, 255, 0.2) 0%,
                rgba(255, 255, 255, 0.2) 30%,
                transparent 60%
              ),
              linear-gradient(
                135deg,
                transparent 40%,
                rgba(255, 255, 255, 0.1) 50%,
                transparent 60%
              )
            `,
            boxShadow:
              "inset 0 1px 2px rgba(255, 255, 255, 0.6), inset 0 -1px 2px rgba(0, 0, 0, 0.1)",
          }}
        />
      )}
    </div>
  );
};

export default CurrencyFlag;
